import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Reveal from "@/components/Reveal";
import { Check } from "lucide-react";

export function Vendors() {
  const [vendors, setVendors] = useState([]);
  useEffect(() => { api.get("/vendors").then((r) => setVendors(r.data)); }, []);
  return (
    <div className="mx-auto max-w-[1400px] px-4 md:px-8 py-10">
      <span className="text-xs font-bold uppercase tracking-widest text-mart-wood">Wood Mart</span>
      <h1 className="mt-1 text-4xl font-extrabold text-midnight">Vendor Collections</h1>
      <p className="mt-1 text-muted-foreground max-w-2xl">Browse approved local furniture vendors. Select a vendor, choose a design, and request minor customization — confirmed by the vendor before your order.</p>
      <div className="mt-8 grid md:grid-cols-2 gap-6">
        {vendors.map((v, i) => (
          <Reveal key={v.vendor_id} delay={i * 0.08}>
            <Link to={`/vendors/${v.vendor_id}`} data-testid={`vendor-${v.vendor_id}`} className="group block rounded-3xl border border-border bg-card overflow-hidden transition-shadow hover:shadow-lg">
              <div className="h-44 overflow-hidden"><img src={v.image} alt={v.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
              <div className="p-6">
                <div className="flex items-center justify-between"><h3 className="text-lg font-extrabold text-midnight">{v.name}</h3><span className="text-sm font-bold text-gold-dark">★ {v.rating}</span></div>
                <p className="text-xs font-semibold text-mart-wood">{v.city}</p>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{v.profile}</p>
                <div className="mt-3 flex flex-wrap gap-2">{v.styles.map((s) => <span key={s} className="rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold">{s}</span>)}</div>
                <p className="mt-3 text-sm font-bold text-midnight">{v.price_range} • {v.delivery_estimate}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  );
}

export function VendorPage() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); api.get(`/vendors/${id}`).then((r) => setV(r.data)).catch(() => setV(null)); }, [id]);
  if (!v) return <div className="py-32 text-center text-muted-foreground">Loading…</div>;

  return (
    <div className="mx-auto max-w-[1400px] px-4 md:px-8 py-10">
      <Link to="/vendors" className="text-sm text-muted-foreground hover:text-midnight">← All vendors</Link>
      <div className="mt-4 grid md:grid-cols-3 gap-6">
        <div className="overflow-hidden rounded-3xl"><img src={v.image} alt={v.name} className="h-full w-full object-cover" /></div>
        <div className="md:col-span-2">
          <h1 className="text-3xl font-extrabold text-midnight">{v.name}</h1>
          <p className="text-sm font-semibold text-mart-wood">{v.city} • ★ {v.rating}</p>
          <p className="mt-3 text-muted-foreground">{v.profile}</p>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            {[["Price range", v.price_range], ["Delivery", v.delivery_estimate], ["Materials", v.materials.join(", ")], ["Service area", v.service_area.join(", ")]].map(([k, val]) => (
              <div key={k} className="rounded-2xl border border-border p-3"><p className="text-xs text-muted-foreground">{k}</p><p className="font-semibold text-midnight">{val}</p></div>
            ))}
          </div>
          {v.customization && <p className="mt-3 flex items-center gap-2 text-sm font-semibold text-mart-home"><Check className="h-4 w-4" /> Customization available</p>}
        </div>
      </div>
      <h2 className="mt-12 mb-6 text-2xl font-extrabold text-midnight">{v.name} Catalogue</h2>
      {v.products?.length ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-5">{v.products.map((p) => <ProductCard key={p.id} product={p} />)}</div>
      ) : <p className="text-muted-foreground">Catalogue coming soon.</p>}
    </div>
  );
}
